/**
 * Unified proposal fulfilment timeline — Target goods-ready / delivery timing.
 * Pure helpers only; persistence lives in server/procurement/proposalFulfilmentTimelineService.
 */

import {
  isProjectBackedProposalSourceValue,
  isRetailProposalSourceValue,
  normalisedProposalSource,
} from "./proposalSources";

export const FULFILMENT_TIMELINE_TYPES = ["unset", "asap", "month_year"] as const;
export type FulfilmentTimelineType = (typeof FULFILMENT_TIMELINE_TYPES)[number];

export const PROCUREMENT_TRACKING_ORIGINS = ["unknown", "portal_native", "legacy_import"] as const;
export type ProcurementTrackingOrigin = (typeof PROCUREMENT_TRACKING_ORIGINS)[number];

export const TIMELINE_CHANGE_KINDS = ["customer_commitment", "internal_forecast"] as const;
export type TimelineChangeKind = (typeof TIMELINE_CHANGE_KINDS)[number];

/** Staff UI label for the shared timing field. */
export const TARGET_GOODS_READY_LABEL = "Target goods-ready / delivery timing";

export type ProposalDisplayKind = "PROJECT" | "RETAIL";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

const MONTH_SHORT = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
] as const;

const YYYY_MM_RE = /^(\d{4})-(0[1-9]|1[0-2])$/;

export function isStrictYearMonth(value: string | null | undefined): value is string {
  return typeof value === "string" && YYYY_MM_RE.test(value.trim());
}

export function assertStrictYearMonth(value: string): string {
  const trimmed = value.trim();
  if (!YYYY_MM_RE.test(trimmed)) {
    throw new Error("Enter a valid month as YYYY-MM (for example 2026-10).");
  }
  return trimmed;
}

/**
 * Parse staff input into canonical YYYY-MM.
 * Accepts: `2026-10`, `Oct 2026`, `October 2026`, `10/2026`, `10-2026`.
 * Rejects ambiguous or invalid values.
 */
export function parseYearMonthInput(raw: string | null | undefined): string {
  const text = String(raw ?? "").trim();
  if (!text) throw new Error("Enter a target month.");

  if (/^asap$/i.test(text)) {
    throw new Error("Use the ASAP option instead of typing ASAP as a month.");
  }

  if (YYYY_MM_RE.test(text)) return text;

  const slash = text.match(/^(\d{1,2})[\/\-](\d{4})$/);
  if (slash) {
    const month = Number(slash[1]);
    const year = Number(slash[2]);
    if (month < 1 || month > 12) throw new Error("Month must be between 1 and 12.");
    if (year < 2000 || year > 2100) throw new Error("Enter a year between 2000 and 2100.");
    return `${year}-${String(month).padStart(2, "0")}`;
  }

  const named = text.match(/^([A-Za-z]+)\s+(\d{4})$/);
  if (named) {
    const token = named[1]!.toLowerCase();
    const year = Number(named[2]);
    if (year < 2000 || year > 2100) throw new Error("Enter a year between 2000 and 2100.");
    let monthIdx = MONTH_NAMES.findIndex((m) => m.toLowerCase() === token);
    if (monthIdx < 0) monthIdx = MONTH_SHORT.findIndex((m) => token.startsWith(m));
    if (monthIdx < 0) throw new Error(`Unrecognised month “${named[1]}”.`);
    // Ambiguous abbreviations like "Ju" that could be June or July.
    if (token.length < 3 && !MONTH_SHORT.includes(token as (typeof MONTH_SHORT)[number])) {
      throw new Error(`Ambiguous month “${named[1]}” — use a full month name or YYYY-MM.`);
    }
    return `${year}-${String(monthIdx + 1).padStart(2, "0")}`;
  }

  throw new Error("Enter a month like Oct 2026, October 2026, or 2026-10.");
}

export function formatFulfilmentMonthDisplay(month: string | null | undefined): string | null {
  if (!isStrictYearMonth(month)) return null;
  const [year, mm] = month!.split("-");
  const idx = Number(mm) - 1;
  return `${MONTH_NAMES[idx]} ${year}`;
}

export function formatFulfilmentTimelineDisplay(input: {
  fulfilmentTimelineType: FulfilmentTimelineType | string | null | undefined;
  targetFulfilmentMonth?: string | null;
  procurementForecastMonth?: string | null;
}): string {
  const type = String(input.fulfilmentTimelineType ?? "unset");
  if (type === "asap") {
    const forecast = formatFulfilmentMonthDisplay(input.procurementForecastMonth);
    return forecast ? `ASAP (forecast ${forecast})` : "ASAP";
  }
  if (type === "month_year") {
    return formatFulfilmentMonthDisplay(input.targetFulfilmentMonth) ?? "Month / year";
  }
  return "Not set";
}

/** Final calendar day of YYYY-MM as Date (local noon to avoid DST edge cases). */
export function finalDayOfYearMonth(yearMonth: string): Date {
  const ym = assertStrictYearMonth(yearMonth);
  const [y, m] = ym.split("-").map(Number) as [number, number];
  const lastDay = new Date(y, m, 0).getDate();
  return new Date(y, m - 1, lastDay, 12, 0, 0, 0);
}

export function finalDayOfYearMonthIso(yearMonth: string): string {
  const d = finalDayOfYearMonth(yearMonth);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Effective planning month for order-by / procurement calculations:
 * - month_year → customer target month
 * - asap → procurement forecast (if set)
 */
export function resolveEffectivePlanningMonth(input: {
  fulfilmentTimelineType: FulfilmentTimelineType | string | null | undefined;
  targetFulfilmentMonth?: string | null;
  procurementForecastMonth?: string | null;
}): string | null {
  const type = String(input.fulfilmentTimelineType ?? "unset");
  if (type === "month_year" && isStrictYearMonth(input.targetFulfilmentMonth)) {
    return input.targetFulfilmentMonth!;
  }
  if (type === "asap" && isStrictYearMonth(input.procurementForecastMonth)) {
    return input.procurementForecastMonth!;
  }
  return null;
}

/**
 * Compatibility projection onto `proposal_headers.expectedCompletionDate`.
 * ASAP without forecast → null (never fabricate a date).
 */
export function deriveCompatibilityExpectedCompletionDate(input: {
  fulfilmentTimelineType: FulfilmentTimelineType | string | null | undefined;
  targetFulfilmentMonth?: string | null;
  procurementForecastMonth?: string | null;
}): string | null {
  const planning = resolveEffectivePlanningMonth(input);
  return planning ? finalDayOfYearMonthIso(planning) : null;
}

export function isTimelineComplete(input: {
  fulfilmentTimelineType: FulfilmentTimelineType | string | null | undefined;
  targetFulfilmentMonth?: string | null;
  procurementTrackingOrigin?: ProcurementTrackingOrigin | string | null;
}): boolean {
  const origin = String(input.procurementTrackingOrigin ?? "unknown");
  if (origin === "legacy_import") return true;
  const type = String(input.fulfilmentTimelineType ?? "unset");
  if (type === "asap") return true;
  if (type === "month_year") return isStrictYearMonth(input.targetFulfilmentMonth);
  return false;
}

/**
 * Validate a proposed timeline write. Portal-native creates may not remain unset.
 */
export function validateFulfilmentTimelineWrite(input: {
  fulfilmentTimelineType: FulfilmentTimelineType | string;
  targetFulfilmentMonth?: string | null;
  procurementForecastMonth?: string | null;
  procurementTrackingOrigin?: ProcurementTrackingOrigin | string | null;
  allowUnset?: boolean;
}): {
  fulfilmentTimelineType: FulfilmentTimelineType;
  targetFulfilmentMonth: string | null;
  procurementForecastMonth: string | null;
} {
  const typeRaw = String(input.fulfilmentTimelineType ?? "").trim();
  if (!(FULFILMENT_TIMELINE_TYPES as readonly string[]).includes(typeRaw)) {
    throw new Error("Select ASAP or a target month.");
  }
  const type = typeRaw as FulfilmentTimelineType;
  const origin = String(input.procurementTrackingOrigin ?? "portal_native");

  let target: string | null = input.targetFulfilmentMonth
    ? String(input.targetFulfilmentMonth).trim() || null
    : null;
  let forecast: string | null = input.procurementForecastMonth
    ? String(input.procurementForecastMonth).trim() || null
    : null;

  if (target) target = assertStrictYearMonth(target);
  if (forecast) forecast = assertStrictYearMonth(forecast);

  if (type === "unset") {
    if (!input.allowUnset && origin === "portal_native") {
      throw new Error(`${TARGET_GOODS_READY_LABEL} is required.`);
    }
    if (target) throw new Error("Unset timing cannot include a target month.");
    return { fulfilmentTimelineType: type, targetFulfilmentMonth: null, procurementForecastMonth: forecast };
  }

  if (type === "asap") {
    if (target) throw new Error("ASAP timing cannot include a target month.");
    return { fulfilmentTimelineType: type, targetFulfilmentMonth: null, procurementForecastMonth: forecast };
  }

  // month_year
  if (!target) throw new Error("Select a target month for Month / year timing.");
  return { fulfilmentTimelineType: type, targetFulfilmentMonth: target, procurementForecastMonth: forecast };
}

/**
 * Classify proposal display as PROJECT or RETAIL from proposal source.
 * Never parse the proposal reference string.
 * Manual PDF uses explicit operational origin semantics → treated as PROJECT for
 * staff badge surfaces when not retail (manual uploads are project-lineage deals).
 */
export function classifyProposalDisplayKind(
  proposalSource: string | null | undefined,
): ProposalDisplayKind {
  if (isRetailProposalSourceValue(proposalSource)) return "RETAIL";
  if (isProjectBackedProposalSourceValue(proposalSource)) return "PROJECT";
  // manual_pdf_upload and any other staff portal source: not retail catalogue.
  const src = normalisedProposalSource(proposalSource);
  if (src === "manual_pdf_upload") return "PROJECT";
  return isRetailProposalSourceValue(src) ? "RETAIL" : "PROJECT";
}

export function buildProcurementTimelineRequiredDedupeKey(familyRootId: number): string {
  return `signal:PROCUREMENT_TIMELINE_REQUIRED:family-${familyRootId}`;
}

export function parseProcurementTimelineRequiredFamilyId(dedupeKey: string): number | null {
  const m = String(dedupeKey ?? "").match(/^signal:PROCUREMENT_TIMELINE_REQUIRED:family-(\d+)$/);
  if (!m) return null;
  const id = Number(m[1]);
  return Number.isFinite(id) && id > 0 ? id : null;
}

/** Compatibility projection for retail handover expectedDeliveryType/Month. */
export function toHandoverCompatibilityTiming(input: {
  fulfilmentTimelineType: FulfilmentTimelineType | string | null | undefined;
  targetFulfilmentMonth?: string | null;
}): { expectedDeliveryType: FulfilmentTimelineType; expectedDeliveryMonth: string | null } {
  const type = String(input.fulfilmentTimelineType ?? "unset") as FulfilmentTimelineType;
  if (type === "asap") return { expectedDeliveryType: "asap", expectedDeliveryMonth: null };
  if (type === "month_year" && isStrictYearMonth(input.targetFulfilmentMonth)) {
    return { expectedDeliveryType: "month_year", expectedDeliveryMonth: input.targetFulfilmentMonth! };
  }
  return { expectedDeliveryType: "unset", expectedDeliveryMonth: null };
}

export function zodFulfilmentTimelineInputShape() {
  return {
    fulfilmentTimelineType: FULFILMENT_TIMELINE_TYPES,
    targetFulfilmentMonth: null as string | null,
    procurementTrackingOrigin: PROCUREMENT_TRACKING_ORIGINS,
  };
}

function normaliseTimelineType(value: FulfilmentTimelineType | string | null | undefined): string {
  const raw = String(value ?? "unset").trim();
  return raw || "unset";
}

function normaliseNullableMonth(value: string | null | undefined): string | null {
  const raw = String(value ?? "").trim();
  return raw || null;
}

function normaliseTrackingOrigin(
  value: ProcurementTrackingOrigin | string | null | undefined,
): string {
  const raw = String(value ?? "unknown").trim();
  return raw || "unknown";
}

/**
 * True when any canonical timeline field differs (type, target, forecast, origin).
 * Compatibility dates and changeKind alone do not count as a change.
 */
export function isMeaningfulTimelineSemanticChange(input: {
  previousType: FulfilmentTimelineType | string | null | undefined;
  nextType: FulfilmentTimelineType | string | null | undefined;
  previousTarget: string | null | undefined;
  nextTarget: string | null | undefined;
  previousForecast: string | null | undefined;
  nextForecast: string | null | undefined;
  previousOrigin: ProcurementTrackingOrigin | string | null | undefined;
  nextOrigin: ProcurementTrackingOrigin | string | null | undefined;
  changeKind?: TimelineChangeKind | string | null | undefined;
}): boolean {
  void input.changeKind;
  const previousType = normaliseTimelineType(input.previousType);
  const nextType = normaliseTimelineType(input.nextType);
  const previousTarget = normaliseNullableMonth(input.previousTarget);
  const nextTarget = normaliseNullableMonth(input.nextTarget);
  const previousForecast = normaliseNullableMonth(input.previousForecast);
  const nextForecast = normaliseNullableMonth(input.nextForecast);
  const previousOrigin = normaliseTrackingOrigin(input.previousOrigin);
  const nextOrigin = normaliseTrackingOrigin(input.nextOrigin);

  return (
    previousType !== nextType ||
    previousTarget !== nextTarget ||
    previousForecast !== nextForecast ||
    previousOrigin !== nextOrigin
  );
}
